import {makeAutoObservable, action, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {Empty} from "data/types/generics";
import {LIVE_SCORING_FETCH_TIMING} from "data/constants";
import type {IChecksums, IChecksumsStore} from "data/stores/checksums/checksums.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";

type IChecksumAction = Record<keyof IChecksums, () => Promise<void>>;

export interface IHOCLiveScoreController extends ViewController {
	subscribeLiveScoring: () => void;
	unsubscribeLiveScoring: () => void;
}

@injectable()
export class HOCLiveScoreController implements IHOCLiveScoreController {
	private _interval: Empty<NodeJS.Timeout>;
	private _isSubscribed: boolean = false;
	private _reactionDisposer: ReturnType<typeof reaction> | null = null;

	constructor(
		@inject(Bindings.ChecksumsStore) private _checksumsStore: IChecksumsStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	private get checksums() {
		return this._checksumsStore.checksums;
	}

	private get _actions(): IChecksumAction {
		return {
			rounds: () => {
				return this._roundsStore.fetchRounds();
			},
		};
	}

	@action
	public subscribeLiveScoring() {
		if (this._isSubscribed) {
			return;
		}

		this._isSubscribed = true;

		void this._checksumsStore.fetch();
		this._interval = setInterval(() => {
			void this._checksumsStore.fetch();
		}, LIVE_SCORING_FETCH_TIMING);
	}

	@action
	public unsubscribeLiveScoring() {
		this._isSubscribed = false;

		if (this._interval) {
			clearInterval(this._interval);
		}

		this._reactionDisposer?.();
	}

	@action
	compareChecksums(checksums: IChecksums, prev: IChecksums): void {
		Object.keys(checksums).forEach((e) => {
			const key = e as keyof IChecksums;
			const oldChecksum = prev[key];

			if (oldChecksum && oldChecksum !== checksums[key]) {
				const action = this._actions[key];
				if (action && typeof action === "function") {
					void action();
				}
			}
		});
	}

	dispose(): void {
		this.unsubscribeLiveScoring();
		this._reactionDisposer?.();
	}

	init(param: void): void {
		this._reactionDisposer?.();
		this._reactionDisposer = reaction(
			() => this.checksums,
			(value, previousValue) => {
				this.compareChecksums(value, previousValue);
			}
		);
	}
}
